import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BadgeCheckIcon, CalendarClockIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon, TrophyIcon } from 'lucide-react';
import { landingImages } from '../../data/landingVisual';

/**
 * Four surfaces, each shown with a fragment of its own interface over the
 * photograph — so the card demonstrates the product rather than describing it.
 * Content is demonstration data.
 */
const CARDS = [{
  id: 'academics',
  title: 'Academics',
  blurb: 'Stay ahead of your semester.',
  image: landingImages.academics,
  alt: 'A university student studying with a laptop and notebooks',
  cta: 'Explore Academics',
  to: '/academics',
  overlay: null
}, {
  id: 'marketplace',
  title: 'Marketplace',
  blurb: 'Buy, sell, discover and earn around campus.',
  image: landingImages.marketplace,
  alt: 'Jollof rice with grilled chicken, a meal sold by a campus vendor',
  cta: 'Explore Marketplace',
  to: '/market',
  overlay: null
}, {
  id: 'housing',
  title: 'Housing',
  blurb: 'Find a place that fits your campus life.',
  image: landingImages.housing,
  alt: 'A modern student residence building',
  cta: 'Explore Housing',
  to: '/housing',
  overlay: 'housing' as const
}, {
  id: 'campus',
  title: 'Campus',
  blurb: 'Everything happening around your campus.',
  image: landingImages.campus,
  alt: 'Students gathered on a university campus plaza',
  cta: 'Explore Campus',
  to: '/social',
  overlay: 'campus' as const
}];
function HousingOverlay() {
  return <div className="absolute inset-x-3 bottom-3 rounded-xl bg-surface/95 px-3 py-2.5 shadow-[0_8px_24px_rgba(15,23,42,0.14)] backdrop-blur" data-id="element-2300" data-mp="3ukbtoe6tufmw">
      <div className="flex items-start justify-between gap-2" data-id="element-2301" data-mp="8ckekigeuqnx">
        <p className="truncate text-caption font-semibold text-ink" data-id="element-2302" data-mp="8eez3vnrsgrh">Cedarwood Residence</p>
        <span className="flex shrink-0 items-center gap-0.5 text-caption text-ink-muted" data-id="element-2303" data-mp="dc6nifp7hkqx">
          <StarIcon aria-hidden className="h-3 w-3 fill-warn-700 text-warn-700" data-id="element-2304" data-mp="3jtp2wj1d9cgk" />
          4.7
        </span>
      </div>
      <p className="cz-num mt-1 text-caption font-bold text-brand-800" data-id="element-2305" data-mp="3evngpp8n8x0v">₦450,000 / year</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-2306" data-mp="3tka0c35b0axw">
        <span className="rounded-full bg-canvas px-2 py-0.5 text-caption text-ink-muted" data-id="element-2307" data-mp="3orgrp46psigj">
          0.8 km from UNILAG
        </span>
        <span className="flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-caption font-medium text-brand-800" data-id="element-2308" data-mp="6cfze6larkeh">
          <BadgeCheckIcon aria-hidden className="h-3 w-3" data-id="element-2309" data-mp="1haffdbggft1" />
          Inspected
        </span>
      </div>
    </div>;
}
function CampusOverlay() {
  return <div className="absolute inset-x-3 top-3 space-y-2" data-id="element-2310" data-mp="bef4bkwoqpdj">
      {[{
      icon: TrophyIcon,
      title: 'Engineering Week',
      meta: 'Mon 17 Aug · 10:00'
    }, {
      icon: CalendarClockIcon,
      title: 'Sports Fiesta',
      meta: 'Sat 22 Aug · 09:00'
    }].map(item => {
      const Icon = item.icon;
      return <div key={item.title} className="flex items-center gap-2 rounded-xl bg-surface/95 px-2.5 py-2 shadow-[0_8px_24px_rgba(15,23,42,0.14)] backdrop-blur" data-id="element-2311" data-mp="smy32m8nhsn0">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-50" data-id="element-2312" data-mp="iw3hzl754z35">
              <Icon aria-hidden className="h-3.5 w-3.5 text-brand-700" data-id="element-2313" data-mp="ntvaagbp6n7v" />
            </span>
            <span className="min-w-0" data-id="element-2314" data-mp="9udh7ordcnsp">
              <span className="block truncate text-caption font-semibold text-ink" data-id="element-2315" data-mp="dxjx7o4a9tcd">
                {item.title}
              </span>
              <span className="block truncate text-caption text-ink-subtle" data-id="element-2316" data-mp="3w3y4j481r51s">{item.meta}</span>
            </span>
          </div>;
    })}
    </div>;
}
export function ProductShowcase() {
  const railRef = useRef<HTMLUListElement>(null);
  const scrollBy = (direction: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: direction * (rail.clientWidth * 0.8),
      behavior: 'smooth'
    });
  };
  return <section aria-labelledby="showcase-heading" className="bg-canvas py-16 lg:py-20" data-id="element-2317" data-mp="3vbx0vyoph92z">
      <div className="mx-auto max-w-7xl px-4 sm:px-6" data-id="element-2318" data-mp="3qfpte1gnvpix">
        <div className="flex items-end justify-between gap-6" data-id="element-2319" data-mp="3mcjs788eoojb">
          <h2 id="showcase-heading" className="text-h2 font-bold tracking-tight text-ink" data-id="element-2320" data-mp="3mf5zd6rdx210">
            One platform. <span className="text-brand-700" data-id="element-2321" data-mp="3cf5a746u3row">Every part</span> of student life.
          </h2>
          <div className="hidden shrink-0 gap-2 lg:flex" data-id="element-2322" data-mp="3ch00ky0vkc0d">
            {([-1, 1] as const).map(direction => <button key={direction} type="button" onClick={() => scrollBy(direction)} aria-label={direction === -1 ? 'Previous' : 'Next'} className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-surface text-ink-muted transition-colors duration-150 ease-out hover:border-line-strong hover:text-ink" data-id="element-2323" data-mp="32p2kbcv0etus">
                {direction === -1 ? <ChevronLeftIcon aria-hidden className="h-5 w-5" data-id="element-2324" data-mp="149mp93n4dapx" /> : <ChevronRightIcon aria-hidden className="h-5 w-5" data-id="element-2325" data-mp="194s9zz4scjcf" />}
              </button>)}
          </div>
        </div>

        <ul ref={railRef} className="mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-3 [overscroll-behavior-x:contain] lg:grid lg:grid-cols-4 lg:overflow-visible lg:pb-0" data-id="element-2326" data-mp="1i7tbfmiphm9r">
          {CARDS.map(card => <li key={card.id} className="w-[78vw] max-w-sm shrink-0 snap-start sm:w-[54vw] lg:w-auto lg:max-w-none" data-id="element-2327" data-mp="qgdh16awqv8z">
              <Link to={card.to} className="group flex h-full flex-col overflow-hidden rounded-card border border-line bg-surface transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-1 hover:border-brand-200 hover:shadow-[0_16px_40px_rgba(15,23,42,0.1)]" data-id="element-2328" data-mp="z8p6aouaccnd">
                <span className="relative block aspect-[4/3] overflow-hidden" data-id="element-2329" data-mp="1a17j3s711j6f">
                  <img src={card.image} alt={card.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]" data-id="element-2330" data-mp="1535zn65ovhuf" />
                  {card.overlay === 'housing' && <HousingOverlay data-id="element-2331" data-mp="r4prdas6hja6" />}
                  {card.overlay === 'campus' && <CampusOverlay data-id="element-2332" data-mp="m5wenkkptwb2" />}
                </span>
                <span className="flex flex-1 flex-col px-5 py-5" data-id="element-2333" data-mp="m7fuja2gm5cb">
                  <span className="block text-h4 font-semibold text-ink" data-id="element-2334" data-mp="106fjyjxsqo9j">{card.title}</span>
                  <span className="mt-1.5 block max-w-[30ch] text-body-sm text-ink-muted" data-id="element-2335" data-mp="w2hkg84k7bfy">
                    {card.blurb}
                  </span>
                  <span className="mt-auto flex items-center gap-1.5 pt-5 text-body-sm font-semibold text-brand-700" data-id="element-2336" data-mp="27ig04uu8svq5">
                    {card.cta}
                    <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-2337" data-mp="2f15r7696s40n" />
                  </span>
                </span>
              </Link>
            </li>)}
        </ul>
      </div>
    </section>;
}